import { createRouter, createWebHistory, RouteRecordRaw } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import { captureAffiliateLink } from '@/services/affiliateAttribution'

// Wrap lazy imports so a stale-chunk 404 triggers a full page reload
// instead of silently breaking navigation. This happens when:
// - A new deploy changes chunk hashes while old tabs are still open
// - The browser purges cached chunks after long idle periods
// - The dev server restarts and invalidates HMR state
function lazyLoad(loader: () => Promise<any>) {
  return () => loader().catch((err: Error) => {
    // Only reload once to avoid infinite loops
    const lastReload = sessionStorage.getItem('chunk-reload')
    const now = Date.now()
    if (!lastReload || now - Number(lastReload) > 10_000) {
      sessionStorage.setItem('chunk-reload', String(now))
      window.location.reload()
    }
    throw err
  })
}

// Route components (lazy loaded)
// "/" is PrintHQ Cloud's Overview, or the previous Dashboard while the
// cloud_dashboard flag is off for the account (views/cloud/HomeView.vue).
const HomeView = lazyLoad(() => import('@/views/cloud/HomeView.vue'))
const CloudLibrary = lazyLoad(() => import('@/views/cloud/CloudLibrary.vue'))
const SyncActivity = lazyLoad(() => import('@/views/cloud/SyncActivity.vue'))
const CloudDevices = lazyLoad(() => import('@/views/cloud/Devices.vue'))
const Backups = lazyLoad(() => import('@/views/cloud/Backups.vue'))
const SharedCollections = lazyLoad(() => import('@/views/cloud/SharedCollections.vue'))
const Community = lazyLoad(() => import('@/views/cloud/Community.vue'))
const Security = lazyLoad(() => import('@/views/cloud/Security.vue'))
const Preferences = lazyLoad(() => import('@/views/cloud/Preferences.vue'))
const SubscriptionView = lazyLoad(() => import('@/views/cloud/SubscriptionView.vue'))
const TrialStatus = lazyLoad(() => import('@/views/cloud/TrialStatus.vue'))
const Pricing = lazyLoad(() => import('@/views/cloud/Pricing.vue'))
const BillingComplete = lazyLoad(() => import('@/views/cloud/BillingComplete.vue'))
const Filaments = lazyLoad(() => import('@/views/Filaments.vue'))

const Printers = lazyLoad(() => import('@/views/Printers.vue'))
const PrinterDetail = lazyLoad(() => import('@/views/PrinterDetail.vue'))
const Projects = lazyLoad(() => import('@/views/Projects.vue'))
const ProjectDetail = lazyLoad(() => import('@/views/ProjectDetail.vue'))
const Prints = lazyLoad(() => import('@/views/Prints.vue'))
const PrintDetail = lazyLoad(() => import('@/views/PrintDetail.vue'))
const Maintenance = lazyLoad(() => import('@/views/Maintenance.vue'))
const Files = lazyLoad(() => import('@/views/Files.vue'))
const Scheduler = lazyLoad(() => import('@/views/Scheduler.vue'))
const PlannerPage = lazyLoad(() => import('@/views/planner/PlannerPage.vue'))
const PlannerVaultReturn = lazyLoad(() => import('@/views/planner/PlannerVaultReturn.vue'))
const KairoPage = lazyLoad(() => import('@/views/kairo/KairoPage.vue'))
const AdminKairo = lazyLoad(() => import('@/views/admin/kairo/AdminKairo.vue'))
const ModelPainter = lazyLoad(() => import('@/views/ModelPainter.vue'))
const Labels = lazyLoad(() => import('@/views/Labels.vue'))
const Chat = lazyLoad(() => import('@/views/ChatRoute.vue'))
const Settings = lazyLoad(() => import('@/views/Settings.vue'))
const Subscription = lazyLoad(() => import('@/views/Subscription.vue'))
const SubscriptionSuccess = lazyLoad(() => import('@/views/SubscriptionSuccess.vue'))
const Login = lazyLoad(() => import('@/views/auth/Login.vue'))
const Register = lazyLoad(() => import('@/views/auth/Register.vue'))
const ForgotPassword = lazyLoad(() => import('@/views/auth/ForgotPassword.vue'))
const ResetPassword = lazyLoad(() => import('@/views/auth/ResetPassword.vue'))
const AuthCallback = lazyLoad(() => import('@/views/auth/AuthCallback.vue'))
const ShareDownload = lazyLoad(() => import('@/views/ShareDownload.vue'))
const VaultShareDownload = lazyLoad(() => import('@/views/VaultShareDownload.vue'))
const ScanRedirect = lazyLoad(() => import('@/views/ScanRedirect.vue'))
const NotFound = lazyLoad(() => import('@/views/NotFound.vue'))

// Admin views (lazy loaded)
const AdminDashboard = lazyLoad(() => import('@/views/admin/AdminDashboard.vue'))
const AdminPrinterTemplates = lazyLoad(() => import('@/views/admin/AdminPrinterTemplates.vue'))
const AdminFilamentBrands = lazyLoad(() => import('@/views/admin/AdminFilamentBrands.vue'))
const AdminFilamentProducts = lazyLoad(() => import('@/views/admin/AdminFilamentProducts.vue'))
const AdminUsers = lazyLoad(() => import('@/views/admin/AdminUsers.vue'))
const AdminFilamentSubmissions = lazyLoad(() => import('@/views/admin/AdminFilamentSubmissions.vue'))
const AdminPrinterSubmissions = lazyLoad(() => import('@/views/admin/AdminPrinterSubmissions.vue'))
const AdminMaterialPresets = lazyLoad(() => import('@/views/admin/AdminMaterialPresets.vue'))
const AdminPrinterManufacturers = lazyLoad(() => import('@/views/admin/AdminPrinterManufacturers.vue'))
const AdminPrinterModels = lazyLoad(() => import('@/views/admin/AdminPrinterModels.vue'))
const AdminAffiliates = lazyLoad(() => import('@/views/admin/AdminAffiliates.vue'))
const AdminGiftCodes = lazyLoad(() => import('@/views/admin/AdminGiftCodes.vue'))
const AdminMulticolorUnits = lazyLoad(() => import('@/views/admin/AdminMulticolorUnits.vue'))
const AdminGlobalCatalogs = lazyLoad(() => import('@/views/admin/AdminGlobalCatalogs.vue'))
const AdminGcodeAnalyzer = lazyLoad(() => import('@/views/admin/AdminGcodeAnalyzer.vue'))
const AdminBridgeReleases = lazyLoad(() => import('@/views/admin/AdminBridgeReleases.vue'))
const AdminPlayground = lazyLoad(() => import('@/views/admin/AdminPlayground.vue'))
const AdminDiscordBot = lazyLoad(() => import('@/views/admin/AdminDiscordBot.vue'))
const AdminFreeTrials = lazyLoad(() => import('@/views/admin/cloud/AdminFreeTrials.vue'))
const AdminSubscriptions = lazyLoad(() => import('@/views/admin/cloud/AdminSubscriptions.vue'))
const AdminPayments = lazyLoad(() => import('@/views/admin/cloud/AdminPayments.vue'))
const AdminStorage = lazyLoad(() => import('@/views/admin/cloud/AdminStorage.vue'))
const AdminSyncActivity = lazyLoad(() => import('@/views/admin/cloud/AdminSyncActivity.vue'))
const AdminDevices = lazyLoad(() => import('@/views/admin/cloud/AdminDevices.vue'))
const AdminBackups = lazyLoad(() => import('@/views/admin/cloud/AdminBackups.vue'))
const AdminUsageLimits = lazyLoad(() => import('@/views/admin/cloud/AdminUsageLimits.vue'))
const AdminPlanner = lazyLoad(() => import('@/views/admin/planner/AdminPlanner.vue'))
const AdminFeedback = lazyLoad(() => import('@/views/admin/cloud/AdminFeedback.vue'))
const AdminFeatureFlags = lazyLoad(() => import('@/views/admin/cloud/AdminFeatureFlags.vue'))
const AdminSettings = lazyLoad(() => import('@/views/admin/cloud/AdminSettings.vue'))
const AdminAuditLogs = lazyLoad(() => import('@/views/admin/cloud/AdminAuditLogs.vue'))
const AffiliateDashboard = lazyLoad(() => import('@/views/AffiliateDashboard.vue'))
const Locations = lazyLoad(() => import('@/views/Locations.vue'))
const LocationDetail = lazyLoad(() => import('@/views/LocationDetail.vue'))
const Teams = lazyLoad(() => import('@/views/Teams.vue'))
const TeamLayout = lazyLoad(() => import('@/layouts/TeamLayout.vue'))
const TeamDashboard = lazyLoad(() => import('@/views/TeamDashboard.vue'))
const TeamMembers = lazyLoad(() => import('@/views/TeamMembers.vue'))
const TeamSettings = lazyLoad(() => import('@/views/TeamSettings.vue'))
const InviteAccept = lazyLoad(() => import('@/views/InviteAccept.vue'))

const routes: RouteRecordRaw[] = [
  // Auth routes (public)
  {
    path: '/login',
    name: 'Login',
    component: Login,
    meta: { requiresAuth: false, layout: 'auth' }
  },
  {
    path: '/register',
    name: 'Register',
    component: Register,
    meta: { requiresAuth: false, layout: 'auth' }
  },
  {
    path: '/forgot-password',
    name: 'ForgotPassword',
    component: ForgotPassword,
    meta: { requiresAuth: false, layout: 'auth' }
  },
  {
    path: '/auth/reset-password',
    name: 'ResetPassword',
    component: ResetPassword,
    meta: { requiresAuth: false, layout: 'auth' }
  },
  {
    path: '/auth/callback',
    name: 'AuthCallback',
    component: AuthCallback,
    meta: { requiresAuth: false, layout: 'auth' }
  },

  // Public: pricing, and where Checkout sends people who subscribed from Vault.
  {
    path: '/pricing',
    name: 'Pricing',
    component: Pricing,
    meta: { requiresAuth: false, layout: 'bare', title: 'Pricing' }
  },
  {
    path: '/billing/complete',
    name: 'BillingComplete',
    component: BillingComplete,
    meta: { requiresAuth: false, layout: 'bare', title: 'Thanks' }
  },

  // App routes (protected)
  {
    path: '/',
    name: 'Dashboard',
    component: HomeView,
    meta: { requiresAuth: true, title: 'Overview', stores: ['printers', 'filaments', 'prints', 'projects', 'maintenance'] }
  },
  // PrintHQ Cloud
  { path: '/library', name: 'CloudLibrary', component: CloudLibrary, meta: { requiresAuth: true, title: 'Cloud Library' } },
  { path: '/sync', name: 'SyncActivity', component: SyncActivity, meta: { requiresAuth: true, title: 'Sync Activity' } },
  { path: '/devices', name: 'Devices', component: CloudDevices, meta: { requiresAuth: true, title: 'Devices' } },
  { path: '/backups', name: 'Backups', component: Backups, meta: { requiresAuth: true, title: 'Backups & Recovery' } },
  { path: '/shared', name: 'SharedCollections', component: SharedCollections, meta: { requiresAuth: true, title: 'Shared Collections' } },
  { path: '/community', name: 'Community', component: Community, meta: { requiresAuth: true, title: 'Community' } },
  { path: '/security', name: 'Security', component: Security, meta: { requiresAuth: true, title: 'Security' } },
  { path: '/preferences', name: 'Preferences', component: Preferences, meta: { requiresAuth: true, title: 'Preferences' } },
  { path: '/trial', name: 'TrialStatus', component: TrialStatus, meta: { requiresAuth: true, title: 'Trial Status' } },
  // Old and alternative addresses (bookmarks, links in emails and in Vault).
  { path: '/dashboard', redirect: '/' },
  { path: '/overview', redirect: '/' },
  { path: '/billing', redirect: '/subscription' },
  { path: '/storefronts', redirect: '/locations' },
  { path: '/signup', redirect: (to) => ({ path: '/register', query: to.query }) },
  {
    path: '/filaments',
    name: 'Filaments',
    component: Filaments,
    meta: { requiresAuth: true, title: 'Filaments', stores: ['filaments', 'filamentBrands', 'customBrands', 'customMaterials', 'customStorageLocations'] }
  },
  {
    path: '/printers',
    name: 'Printers',
    component: Printers,
    meta: { requiresAuth: true, title: 'Printers', stores: ['printers'] }
  },
  {
    path: '/printers/:id',
    name: 'PrinterDetail',
    component: PrinterDetail,
    meta: { requiresAuth: true, title: 'Printer Details', stores: ['printers', 'prints', 'maintenance'] }
  },
  {
    path: '/projects',
    name: 'Projects',
    component: Projects,
    meta: { requiresAuth: true, title: 'Projects', stores: ['projects', 'prints', 'printers', 'filaments'] }
  },
  {
    path: '/projects/:id',
    name: 'ProjectDetail',
    component: ProjectDetail,
    meta: { requiresAuth: true, title: 'Project Details', stores: ['projects', 'prints', 'printers', 'filaments'] }
  },
  {
    path: '/prints',
    name: 'Prints',
    component: Prints,
    meta: { requiresAuth: true, title: 'Prints', stores: ['prints', 'printers', 'filaments'] }
  },
  {
    path: '/prints/:id',
    name: 'PrintDetail',
    component: PrintDetail,
    meta: { requiresAuth: true, title: 'Print Details', stores: ['prints', 'printers', 'filaments'] }
  },
  {
    path: '/maintenance',
    name: 'Maintenance',
    component: Maintenance,
    meta: { requiresAuth: true, title: 'Maintenance', stores: ['maintenance', 'printers'] }
  },
  {
    path: '/files',
    name: 'Files',
    component: Files,
    meta: { requiresAuth: true, title: 'Files', stores: ['files'] }
  },
  {
    path: '/planner',
    name: 'Planner',
    component: PlannerPage,
    meta: { requiresAuth: true, title: 'Planner' }
  },
  // Where Google Calendar's sign-in sends PrintHQ Vault's browser tab (the
  // browser may not be signed in here). Path kept in step with the callback.
  {
    path: '/planner/vault-return',
    name: 'PlannerVaultReturn',
    component: PlannerVaultReturn,
    meta: { requiresAuth: false, layout: 'bare', title: 'Google Calendar' }
  },
  {
    path: '/scheduler',
    name: 'Scheduler',
    component: Scheduler,
    meta: { requiresAuth: true, requiresBridge: true, title: 'Scheduler', stores: ['printers', 'printJobs'] }
  },
  {
    path: '/painter',
    name: 'ModelPainter',
    component: ModelPainter,
    meta: { requiresAuth: true, requiresAdmin: true, title: 'Model Painter', desktopOnly: true, stores: ['files', 'filaments'] }
  },
  {
    path: '/labels',
    name: 'Labels',
    component: Labels,
    meta: { requiresAuth: true, title: 'Labels', stores: ['filaments'] }
  },
  { path: '/kairo', name: 'Kairo', component: KairoPage, meta: { requiresAuth: true, title: 'Kairo' } },
  {
    path: '/chat',
    name: 'Chat',
    component: Chat,
    meta: { requiresAuth: true, title: 'AI Chat', stores: ['printers', 'filaments'] }
  },
  {
    path: '/settings',
    name: 'Settings',
    component: Settings,
    meta: { requiresAuth: true, title: 'Settings' }
  },
  {
    path: '/subscription',
    name: 'Subscription',
    component: SubscriptionView,
    meta: { requiresAuth: true, title: 'Subscription & Billing' }
  },
  {
    path: '/subscription/legacy',
    name: 'SubscriptionLegacy',
    component: Subscription,
    meta: { requiresAuth: true, title: 'Subscription' }
  },
  {
    path: '/subscription/success',
    name: 'SubscriptionSuccess',
    component: SubscriptionSuccess,
    meta: { requiresAuth: true, title: 'Subscription Activated' }
  },

  // Affiliate & Locations
  {
    path: '/affiliate',
    name: 'AffiliateDashboard',
    component: AffiliateDashboard,
    meta: { requiresAuth: true, title: 'Affiliate Dashboard' }
  },
  {
    path: '/locations',
    name: 'Locations',
    component: Locations,
    meta: { requiresAuth: true, title: 'Locations', stores: ['locations', 'products', 'sales'] }
  },
  {
    path: '/locations/:id',
    name: 'LocationDetail',
    component: LocationDetail,
    meta: { requiresAuth: true, title: 'Location Details', stores: ['locations', 'products', 'sales'] }
  },

  // Teams
  {
    path: '/teams',
    name: 'Teams',
    component: Teams,
    meta: { requiresAuth: true, requiresAdmin: true, title: 'Teams', stores: ['teams'] }
  },
  {
    path: '/teams/:id',
    component: TeamLayout,
    meta: { requiresAuth: true, requiresAdmin: true },
    children: [
      { path: '', name: 'TeamDashboard', component: TeamDashboard, meta: { title: 'Team Dashboard' } },
      { path: 'members', name: 'TeamMembers', component: TeamMembers, meta: { title: 'Team Members' } },
      { path: 'settings', name: 'TeamSettings', component: TeamSettings, meta: { title: 'Team Settings' } },
      { path: 'printers', name: 'TeamPrinters', component: Printers, meta: { title: 'Team Printers' } },
      { path: 'printers/:printerId', name: 'TeamPrinterDetail', component: PrinterDetail, meta: { title: 'Team Printer Details' } },
      { path: 'prints', name: 'TeamPrints', component: Prints, meta: { title: 'Team Prints' } },
      { path: 'prints/:printId', name: 'TeamPrintDetail', component: PrintDetail, meta: { title: 'Team Print Details' } },
      { path: 'projects', name: 'TeamProjects', component: Projects, meta: { title: 'Team Projects' } },
      { path: 'projects/:projectId', name: 'TeamProjectDetail', component: ProjectDetail, meta: { title: 'Team Project Details' } },
      { path: 'filaments', name: 'TeamFilaments', component: Filaments, meta: { title: 'Team Filaments' } },
      { path: 'maintenance', name: 'TeamMaintenance', component: Maintenance, meta: { title: 'Team Maintenance' } },
      { path: 'files', name: 'TeamFiles', component: Files, meta: { title: 'Team Files' } },
      { path: 'scheduler', name: 'TeamScheduler', component: Scheduler, meta: { title: 'Team Scheduler' } },
      { path: 'locations', name: 'TeamLocations', component: Locations, meta: { title: 'Team Locations' } },
      { path: 'locations/:locationId', name: 'TeamLocationDetail', component: LocationDetail, meta: { title: 'Team Location Details' } },
    ]
  },
  {
    path: '/invite/:token',
    name: 'InviteAccept',
    component: InviteAccept,
    meta: { requiresAuth: false, layout: 'bare', title: 'Team Invitation' }
  },

  // Admin routes
  {
    path: '/admin',
    name: 'AdminDashboard',
    component: AdminDashboard,
    meta: { requiresAuth: true, requiresAdmin: true, layout: 'admin', title: 'Admin Dashboard' }
  },
  {
    path: '/admin/printer-templates',
    name: 'AdminPrinterTemplates',
    component: AdminPrinterTemplates,
    meta: { requiresAuth: true, requiresAdmin: true, layout: 'admin', title: 'Printer Templates' }
  },
  {
    path: '/admin/filament-brands',
    name: 'AdminFilamentBrands',
    component: AdminFilamentBrands,
    meta: { requiresAuth: true, requiresAdmin: true, layout: 'admin', title: 'Filament Brands' }
  },
  {
    path: '/admin/filament-products',
    name: 'AdminFilamentProducts',
    component: AdminFilamentProducts,
    meta: { requiresAuth: true, requiresAdmin: true, layout: 'admin', title: 'Filament Products' }
  },
  {
    path: '/admin/filament-submissions',
    name: 'AdminFilamentSubmissions',
    component: AdminFilamentSubmissions,
    meta: { requiresAuth: true, requiresAdmin: true, layout: 'admin', title: 'Filament Submissions' }
  },
  {
    path: '/admin/printer-submissions',
    name: 'AdminPrinterSubmissions',
    component: AdminPrinterSubmissions,
    meta: { requiresAuth: true, requiresAdmin: true, layout: 'admin', title: 'Printer Submissions' }
  },
  {
    path: '/admin/users',
    name: 'AdminUsers',
    component: AdminUsers,
    meta: { requiresAuth: true, requiresAdmin: true, layout: 'admin', title: 'User Management' }
  },
  {
    path: '/admin/global-catalogs',
    name: 'AdminGlobalCatalogs',
    component: AdminGlobalCatalogs,
    meta: { requiresAuth: true, requiresAdmin: true, layout: 'admin', title: 'Global Catalogs' }
  },
  {
    path: '/admin/material-presets',
    name: 'AdminMaterialPresets',
    component: AdminMaterialPresets,
    meta: { requiresAuth: true, requiresAdmin: true, layout: 'admin', title: 'Material Presets' }
  },
  {
    path: '/admin/printer-manufacturers',
    name: 'AdminPrinterManufacturers',
    component: AdminPrinterManufacturers,
    meta: { requiresAuth: true, requiresAdmin: true, layout: 'admin', title: 'Printer Manufacturers' }
  },
  {
    path: '/admin/printer-models',
    name: 'AdminPrinterModels',
    component: AdminPrinterModels,
    meta: { requiresAuth: true, requiresAdmin: true, layout: 'admin', title: 'Printer Models' }
  },
  {
    path: '/admin/affiliates',
    name: 'AdminAffiliates',
    component: AdminAffiliates,
    meta: { requiresAuth: true, requiresAdmin: true, layout: 'admin', title: 'Affiliates' }
  },
  {
    path: '/admin/gift-codes',
    name: 'AdminGiftCodes',
    component: AdminGiftCodes,
    meta: { requiresAuth: true, requiresAdmin: true, layout: 'admin', title: 'Gift Codes' }
  },
  {
    path: '/admin/multicolor-units',
    name: 'AdminMulticolorUnits',
    component: AdminMulticolorUnits,
    meta: { requiresAuth: true, requiresAdmin: true, layout: 'admin', title: 'Multicolor Units' }
  },
  {
    path: '/admin/gcode-analyzer',
    name: 'AdminGcodeAnalyzer',
    component: AdminGcodeAnalyzer,
    meta: { requiresAuth: true, requiresAdmin: true, layout: 'admin', title: 'G-code Analyzer' }
  },
  {
    path: '/admin/bridge-releases',
    name: 'AdminBridgeReleases',
    component: AdminBridgeReleases,
    meta: { requiresAuth: true, requiresAdmin: true, layout: 'admin', title: 'Bridge Releases' }
  },
  {
    path: '/admin/playground',
    name: 'AdminPlayground',
    component: AdminPlayground,
    meta: { requiresAuth: true, requiresAdmin: true, layout: 'admin', title: 'Playground' }
  },
  // PrintHQ Cloud admin
  ...([
    ['trials', 'AdminFreeTrials', AdminFreeTrials, 'Free Trials'],
    ['subscriptions', 'AdminSubscriptions', AdminSubscriptions, 'Subscriptions'],
    ['payments', 'AdminPayments', AdminPayments, 'Payments'],
    ['storage', 'AdminStorage', AdminStorage, 'Storage'],
    ['sync', 'AdminSyncActivity', AdminSyncActivity, 'Sync Activity'],
    ['devices', 'AdminDevices', AdminDevices, 'Devices'],
    ['backups', 'AdminBackups', AdminBackups, 'Backups'],
    ['usage', 'AdminUsageLimits', AdminUsageLimits, 'Usage & Limits'],
    ['planner', 'AdminPlanner', AdminPlanner, 'Planner'],
    ['feedback', 'AdminFeedback', AdminFeedback, 'User Feedback'],
    ['flags', 'AdminFeatureFlags', AdminFeatureFlags, 'Feature Flags'],
    ['settings', 'AdminSettings', AdminSettings, 'Settings'],
    ['audit', 'AdminAuditLogs', AdminAuditLogs, 'Audit Logs'],
  ] as const).map(([path, name, component, title]): RouteRecordRaw => ({
    path: `/admin/${path}`,
    name,
    component,
    meta: { requiresAuth: true, requiresAdmin: true, layout: 'admin', title },
  })),
  { path: '/admin/kairo/:tab?', name: 'AdminKairo', component: AdminKairo, meta: { requiresAuth: true, requiresAdmin: true, layout: 'admin', title: 'Kairo AI' } },
  {
    path: '/admin/integrations/discord',
    name: 'AdminDiscordBot',
    component: AdminDiscordBot,
    meta: { requiresAuth: true, requiresAdmin: true, layout: 'admin', title: 'Discord Bot' }
  },

  // Scan code resolver (redirects to filaments with query params)
  {
    path: '/scan/:code',
    name: 'ScanRedirect',
    component: ScanRedirect,
    meta: { requiresAuth: true, title: 'Scanning...', stores: ['filaments'] }
  },

  // Public share download (no auth required)
  {
    path: '/share/:token',
    name: 'ShareDownload',
    component: ShareDownload,
    meta: { requiresAuth: false, layout: 'bare', title: 'Download Shared File' }
  },

  // Public PrintHQ Vault share link (no auth required; token validated by vault-share fn)
  {
    path: '/vault/s/:token',
    name: 'VaultShareDownload',
    component: VaultShareDownload,
    meta: { requiresAuth: false, layout: 'bare', title: 'Download Shared File' }
  },

  // 404
  {
    path: '/:pathMatch(.*)*',
    name: 'NotFound',
    component: NotFound,
    meta: { requiresAuth: false }
  }
]

const router = createRouter({
  history: createWebHistory(),
  routes,
  scrollBehavior(to, from, savedPosition) {
    if (savedPosition) {
      return savedPosition
    }
    return { top: 0 }
  }
})

// Navigation guards — ONLY handles auth + title. No store loading here.
// Each page component loads its own stores in onMounted so navigation
// is never blocked by hung network requests after tab idle.
//
// IMPORTANT: This guard must NEVER block for more than ~3s. A hung guard
// makes the entire app unresponsive (sidebar links, buttons, everything).
router.beforeEach(async (to, _from, next) => {
  // Capture the referral before an authentication redirect can remove the query.
  // Validation and account locking happen server-side after sign-in.
  captureAffiliateLink(to.query.ref)
  const authStore = useAuthStore()

  // Wait for INITIAL auth init only (first app boot).
  // Cap at 3s — if auth is still loading after that, proceed anyway.
  // This prevents the guard from blocking navigation after tab idle
  // if something sets loading=true unexpectedly.
  if (authStore.loading) {
    await Promise.race([
      new Promise<void>(resolve => {
        const unwatch = authStore.$subscribe((_mutation, state) => {
          if (!state.loading) {
            unwatch()
            resolve()
          }
        })
      }),
      new Promise<void>(resolve => setTimeout(resolve, 3000)),
    ])
  }

  // Kick off a session refresh if the JWT is stale (e.g. after tab
  // backgrounding). Fire-and-forget — never block navigation. The
  // visibilitychange handler and useStoreLoader already handle refreshes;
  // this is just an eager nudge so the next Supabase call uses a fresh token.
  if (authStore.isAuthenticated) {
    authStore.ensureFreshSession().catch(() => {})
  }

  const requiresAuth = to.meta.requiresAuth !== false
  const requiresAdmin = to.meta.requiresAdmin === true
  const requiresBridge = to.meta.requiresBridge === true
  const isAuthenticated = authStore.isAuthenticated
  const BRIDGE_TIERS = ['hobby', 'personal', 'maker', 'commercial', 'farm']

  // Update document title
  const baseTitle = 'PrintHQ'
  document.title = to.meta.title
    ? `${to.meta.title} | ${baseTitle}`
    : baseTitle

  if (to.meta.desktopOnly && window.innerWidth < 1024) {
    next({ name: 'Dashboard' })
  } else if (requiresAuth && !isAuthenticated) {
    next({ name: 'Login', query: { redirect: to.fullPath } })
  } else if (requiresAdmin && !authStore.isAdmin) {
    next({ name: 'Dashboard' })
  } else if (requiresBridge && !authStore.isAdmin && !BRIDGE_TIERS.includes(authStore.subscriptionTier)) {
    next({ name: 'Dashboard' })
  } else if (!requiresAuth && isAuthenticated && to.name === 'Login') {
    next({ name: 'Dashboard' })
  } else {
    next()
  }
})

export default router
